import { TypographyCaption } from '@heidi/design-system/typography';
import { Paperclip } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import { REQUEST_PROTECTED_ACTION_APPROVAL_TOOL_NAME } from '../../contracts/child-run-approval.types';
import { REQUEST_HUMAN_APPROVAL_TOOL_NAME } from '../../contracts/projects/approval-tool-names';
import {
  readMergeApprovalFields,
  readMergeSessionsApprovalFields,
} from '../projects/components/ApprovalRequestBlock.display';
import { MergePatientProfilesApprovalDetails } from '../projects/components/MergePatientProfilesApprovalDetails';
import {
  MergeSessionRow,
  MergeSessionsApprovalDetails,
} from '../projects/components/MergeSessionsApprovalDetails';
import { NotePushApprovalDetails } from '../projects/components/NotePushApprovalDetails';
import { DraftBody, type DraftApprovalBodyRenderArgs } from './draft-body';
import { LabelledContextPanel } from './labelled-context-panel';
import { MailBodyMarkdown } from './mail-compose-approval-layout';
import {
  blocksOfKind,
  type ApprovalPresentation,
  type EmailBlock,
  type FieldChangeBlock,
  type MergeCandidatesBlock,
  type PresentationBlock,
  type TextChangeBlock,
} from './tool-call-presentation';
import { useDraftPreview } from './use-draft-preview';

/**
 * The server-built body of an approval card: each block in order, drawn by its
 * kind. The patient, session and EHR blocks draw nothing here; they are the
 * card's subtitle (`presentationSubject`). One component for every card surface
 * (chat and the desktop overlay), so a surface can't show a different body.
 */
export interface PresentationDetailsProps {
  blocks: readonly PresentationBlock[];
  /** The call's input: a text change reads it to show a deletion, a merge which profile stays. */
  toolInput?: Record<string, unknown>;
  /** A text change's text, drawn the way the draft card draws it (TipTap). */
  renderDraftBody?: (args: DraftApprovalBodyRenderArgs) => ReactNode;
  /** A merged session the server sent no title for, looked up by id. */
  renderMergeSessionRow?: (sessionId: string) => ReactNode;
}

type SubjectBlock = Extract<PresentationBlock, { kind: 'patient' | 'session' | 'ehr' }>;
type BodyBlock = Exclude<PresentationBlock, SubjectBlock | FieldChangeBlock>;
type BodySection =
  | { kind: 'field_changes'; changes: FieldChangeBlock[] }
  | { kind: 'block'; block: BodyBlock };

const isSubjectBlock = (block: PresentationBlock): block is SubjectBlock =>
  block.kind === 'patient' || block.kind === 'session' || block.kind === 'ehr';

/** Consecutive field changes share one table. */
const bodySections = (blocks: readonly PresentationBlock[]): BodySection[] => {
  const sections: BodySection[] = [];
  for (const block of blocks) {
    if (isSubjectBlock(block)) continue;
    const last = sections[sections.length - 1];
    if (block.kind !== 'field_change') sections.push({ kind: 'block', block });
    else if (last?.kind === 'field_changes') last.changes.push(block);
    else sections.push({ kind: 'field_changes', changes: [block] });
  }
  return sections;
};

/** Whether `PresentationDetails` renders anything for `blocks`. */
export const hasPresentationDetails = (blocks: readonly PresentationBlock[]): boolean =>
  blocks.some((block) => !isSubjectBlock(block));

const isKnownTimeZone = (timeZone: string): boolean => {
  try {
    new Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch {
    return false;
  }
};

const formatSessionStart = (
  intl: IntlShape,
  startsAt: string,
  timeZone: string | null | undefined
): string =>
  // react-intl swallows an unknown zone and returns `String(date)`, so check
  // it first; without a zone, the offset in `startsAt` still places the visit.
  intl.formatDate(startsAt, {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...(timeZone && isKnownTimeZone(timeZone) ? { timeZone } : {}),
  });

/** A calendar date, formatted in UTC to keep the day the server sent. */
const formatCalendarDate = (intl: IntlShape, date: string): string =>
  intl.formatDate(`${date}T00:00:00Z`, { dateStyle: 'medium', timeZone: 'UTC' });

/**
 * "Cerner · Jo Shmo, F, 44 · Knee review · 6 Oct 2026, 2:30 pm", or '' when the
 * blocks name no one.
 */
export const presentationSubject = (
  intl: IntlShape,
  blocks: readonly PresentationBlock[]
): string => {
  const [ehr] = blocksOfKind(blocks, 'ehr');
  const [patient] = blocksOfKind(blocks, 'patient');
  const [session] = blocksOfKind(blocks, 'session');
  return [
    ehr?.display_name,
    patient?.display_name,
    session?.title,
    session?.starts_at
      ? formatSessionStart(intl, session.starts_at, session.timezone)
      : undefined,
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' · ');
};

const OWN_HEADER_TOOL_NAMES: ReadonlySet<string> = new Set([
  REQUEST_HUMAN_APPROVAL_TOOL_NAME,
  REQUEST_PROTECTED_ACTION_APPROVAL_TOOL_NAME,
]);

/**
 * What the presentation puts in a card's header: the tool's fixed title, which
 * is the header whenever the server sent one, and the subject the EHR, patient
 * and session blocks name, which is the subtitle whenever it names anyone. The
 * proactive and protected-action gates keep their own header, so they get `null`.
 */
export const readPresentedHeader = (
  toolName: string,
  presentation: ApprovalPresentation,
  intl: IntlShape
): { title: string | undefined; subject: string } | null =>
  OWN_HEADER_TOOL_NAMES.has(toolName)
    ? null
    : {
        title: presentation.title,
        subject: presentationSubject(intl, presentation.blocks),
      };

const profileFieldLabel = (intl: IntlShape, field: string): string => {
  switch (field) {
    case 'first_name':
      return intl.formatMessage({ defaultMessage: 'First name', id: 'pONqz8J25C' });
    case 'last_name':
      return intl.formatMessage({ defaultMessage: 'Last name', id: 'txUL0FMLAE' });
    case 'date_of_birth':
      return intl.formatMessage({ defaultMessage: 'Date of birth', id: 'GFTdXwO9W3' });
    case 'gender':
      return intl.formatMessage({ defaultMessage: 'Gender', id: 'm8/n8cq9i9' });
    case 'demographic_string':
      return intl.formatMessage({
        defaultMessage: 'Patient identifier',
        id: 'aWv7G4OvhI',
      });
    case 'phone':
      return intl.formatMessage({ defaultMessage: 'Phone', id: 'O95R3ZtV32' });
    case 'email':
      return intl.formatMessage({ defaultMessage: 'Email', id: 'sy+pv5U9ls' });
    case 'current_medications':
      return intl.formatMessage({
        defaultMessage: 'Current medications',
        id: 'WYquwaFZfT',
      });
    case 'allergies':
      return intl.formatMessage({ defaultMessage: 'Allergies', id: 'mWM48NqfRS' });
    case 'past_medical_history':
      return intl.formatMessage({
        defaultMessage: 'Past medical history',
        id: 'bL9ArTn3bc',
      });
    case 'additional_context':
      return intl.formatMessage({
        defaultMessage: 'Additional context',
        id: 'x9S/k+YsvF',
      });
    case 'an_acc_class':
      return intl.formatMessage({ defaultMessage: 'AN-ACC class', id: 'x5vDHwTCP5' });
    case 'use_in_doc_and_note_gen':
      return intl.formatMessage({
        defaultMessage: 'Use patient details in notes',
        id: 'CJH4lRYo6U',
      });
    case 'remember_consent':
      return intl.formatMessage({ defaultMessage: 'Remember consent', id: 'm+l6F5OJ/2' });
    case 'ethnicity':
      return intl.formatMessage({ defaultMessage: 'Ethnicity', id: 'XtCAFoZHQg' });
    case 'first_language':
      return intl.formatMessage({ defaultMessage: 'First language', id: 'ZCY1HzKUqa' });
    case 'accent':
      return intl.formatMessage({ defaultMessage: 'Accent', id: '6nU/oC+to3' });
    case 'postcode_prefix':
      return intl.formatMessage({ defaultMessage: 'Postcode', id: 'FJhjgzPjGv' });
    default:
      return field.replaceAll('_', ' ').replace(/^./, (first) => first.toUpperCase());
  }
};

const profileFieldValue = (
  intl: IntlShape,
  field: string,
  value: FieldChangeBlock['before']
): string | null => {
  if (value === null) return null;
  if (typeof value === 'boolean') {
    return value
      ? intl.formatMessage({ defaultMessage: 'Yes', id: 'a5msuh5ORp' })
      : intl.formatMessage({ defaultMessage: 'No', id: 'oUWADlOjJt' });
  }
  if (field === 'date_of_birth' && typeof value === 'string') {
    return formatCalendarDate(intl, value);
  }
  return String(value);
};

const ProfileChangeRow = ({ change }: { change: FieldChangeBlock }) => {
  const intl = useIntl();
  const { field } = change;
  const before = profileFieldValue(intl, field, change.before);
  const after = profileFieldValue(intl, field, change.after);
  return (
    <div className="flex flex-col gap-0.5" data-testid={`presentation-change-${field}`}>
      <dt>
        <TypographyCaption className="font-medium text-text-primary">
          {profileFieldLabel(intl, field)}
        </TypographyCaption>
      </dt>
      <dd className="flex flex-col gap-0.5 text-sm leading-snug [overflow-wrap:anywhere]">
        <span className="whitespace-pre-wrap text-text-secondary line-through decoration-text-secondary/60">
          {before ??
            intl.formatMessage({
              defaultMessage: 'Not set',
              id: '4TwOQ7brOr',
              description:
                'Approval card: a patient profile field with no value before the change',
            })}
        </span>
        <span className="whitespace-pre-wrap text-text-primary">
          {after ??
            intl.formatMessage({
              defaultMessage: 'Cleared',
              id: 'ZvBhtL9vJy',
              description: 'Approval card: a patient profile field the change clears',
            })}
        </span>
      </dd>
    </div>
  );
};

const FieldChangesDetails = ({ changes }: { changes: FieldChangeBlock[] }) => {
  const intl = useIntl();
  return (
    <LabelledContextPanel
      label={intl.formatMessage({
        defaultMessage: 'What will change',
        id: 'F+Lnh2ZHAL',
        description:
          'Approval card: label above each patient profile field before and after',
      })}
    >
      <dl className="flex flex-col gap-2">
        {changes.map((change) => (
          <ProfileChangeRow key={change.field} change={change} />
        ))}
      </dl>
    </LabelledContextPanel>
  );
};

const TextChangeDetails = ({
  change,
  toolInput,
  renderDraftBody,
}: {
  change: TextChangeBlock;
  toolInput: Record<string, unknown>;
  renderDraftBody: PresentationDetailsProps['renderDraftBody'];
}) => {
  const { displayBody, tintAgainst, removesOnly } = useDraftPreview({
    toolInput,
    draft: null,
    serverPreview: change,
  });
  if (!displayBody) return null;
  return (
    <DraftBody
      body={displayBody}
      isStreaming={false}
      tintAgainst={tintAgainst}
      {...(removesOnly ? { removal: true } : {})}
      renderBody={renderDraftBody}
    />
  );
};

const EmailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-0.5">
    <dt>
      <TypographyCaption className="font-medium text-text-secondary">
        {label}
      </TypographyCaption>
    </dt>
    <dd className="break-words text-sm leading-snug text-text-primary [overflow-wrap:anywhere]">
      {value}
    </dd>
  </div>
);

const EmailDetails = ({ email }: { email: EmailBlock }) => {
  const intl = useIntl();
  const to = email.to.join(', ');
  const content = email.content?.trim();
  return (
    <LabelledContextPanel
      label={intl.formatMessage({
        defaultMessage: 'Email',
        id: '+Ru2BhJTnW',
        description: 'Approval card: label above the email a call sends',
      })}
    >
      <div className="flex flex-col gap-2" data-testid="presentation-email">
        <dl className="flex flex-col gap-2">
          {to && (
            <EmailRow
              label={intl.formatMessage({ defaultMessage: 'To', id: '9j3hXO4Ojb' })}
              value={to}
            />
          )}
          {email.subject.trim() && (
            <EmailRow
              label={intl.formatMessage({ defaultMessage: 'Subject', id: 'LLtKhppiyE' })}
              value={email.subject}
            />
          )}
        </dl>
        {content && <MailBodyMarkdown body={content} testId="presentation-email-body" />}
        {email.has_attachment && (
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <Paperclip className="size-3.5 shrink-0" />
            <span data-testid="presentation-email-attachment">
              {intl.formatMessage({
                defaultMessage: 'Completed PDF form attached',
                id: 'pAK8lLHsZm',
                description:
                  'Session email approval: the PDF form rides as an attachment',
              })}
            </span>
          </div>
        )}
      </div>
    </LabelledContextPanel>
  );
};

const candidateLabel = (
  intl: IntlShape,
  row: MergeCandidatesBlock['rows'][number] | undefined
): string | undefined =>
  [row?.name, row?.date_of_birth && formatCalendarDate(intl, row.date_of_birth)]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' · ') || undefined;

/**
 * The merge card's rows, named from the server's read. Which record stays and
 * which folds in still comes from the input, which is what executes.
 */
const MergeCandidatesDetails = ({
  block,
  toolInput,
  renderMergeSessionRow,
}: {
  block: MergeCandidatesBlock;
  toolInput: Record<string, unknown>;
  renderMergeSessionRow: PresentationDetailsProps['renderMergeSessionRow'];
}) => {
  const intl = useIntl();
  const rowFor = (id: string | undefined) => block.rows.find((row) => row.id === id);
  if (block.target === 'sessions') {
    return (
      <MergeSessionsApprovalDetails
        fields={readMergeSessionsApprovalFields(toolInput)}
        renderSession={(sessionId) => {
          const row = rowFor(sessionId);
          if (!row?.title?.trim() && renderMergeSessionRow) {
            return renderMergeSessionRow(sessionId);
          }
          return (
            <MergeSessionRow
              key={sessionId}
              sessionId={sessionId}
              name={row?.title}
              createdTimeLocal={row?.started_at}
            />
          );
        }}
      />
    );
  }
  const fields = readMergeApprovalFields(toolInput);
  return (
    <MergePatientProfilesApprovalDetails
      fields={fields}
      targetName={candidateLabel(intl, rowFor(fields.targetId))}
      sourceName={candidateLabel(intl, rowFor(fields.sourceId))}
    />
  );
};

const BlockDetails = ({
  block,
  toolInput,
  renderDraftBody,
  renderMergeSessionRow,
}: {
  block: BodyBlock;
  toolInput: Record<string, unknown>;
  renderDraftBody: PresentationDetailsProps['renderDraftBody'];
  renderMergeSessionRow: PresentationDetailsProps['renderMergeSessionRow'];
}): ReactNode => {
  switch (block.kind) {
    case 'text_change':
      return (
        <TextChangeDetails
          change={block}
          toolInput={toolInput}
          renderDraftBody={renderDraftBody}
        />
      );
    case 'email':
      return <EmailDetails email={block} />;
    case 'merge_candidates':
      return (
        <MergeCandidatesDetails
          block={block}
          toolInput={toolInput}
          renderMergeSessionRow={renderMergeSessionRow}
        />
      );
    case 'note_push_rows':
      return <NotePushApprovalDetails rows={block.rows} />;
  }
};

export const PresentationDetails = ({
  blocks,
  toolInput = {},
  renderDraftBody,
  renderMergeSessionRow,
}: PresentationDetailsProps): ReactNode => {
  const sections = bodySections(blocks);
  if (sections.length === 0) return null;
  return (
    <div className="flex flex-col gap-2" data-testid="presentation-details">
      {sections.map((section, index) =>
        section.kind === 'field_changes' ? (
          <FieldChangesDetails key={`field_changes-${index}`} changes={section.changes} />
        ) : (
          <BlockDetails
            key={`${section.block.kind}-${index}`}
            block={section.block}
            toolInput={toolInput}
            renderDraftBody={renderDraftBody}
            renderMergeSessionRow={renderMergeSessionRow}
          />
        )
      )}
    </div>
  );
};
